import API_BASE_URL from '../config';
import { commentTable } from '../db/entityCache';
import * as hazardGateway from '../db/hazardGateway';
import { getAuthHeaders } from '../utils/authHeaders';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import eventEmitter, { EVENTS } from './eventService';

export interface UserProfile {
  userId: string;
  username: string;
  displayName: string;
  email?: string;
  profileImageUrl?: string;
}

export interface UpdateProfileRequest {
  displayName?: string;
  profileImage?: string; // Base64 encoded image
}

class UserProfileService {
  private readonly USERNAME_CACHE_PREFIX = 'username_cache_v1_';
  private memoryUsernameCache = new Map<string, string>();
  private profileSelfInflight: Promise<UserProfile> | null = null;

  private getCacheKey(userId: string): string {
    return `${this.USERNAME_CACHE_PREFIX}${userId}`;
  }

  private normalizeUsername(value?: string | null): string | null {
    if (!value) return null;
    const normalized = String(value).trim();
    if (!normalized) return null;
    if (normalized.toLowerCase() === 'unknown user') return null;
    return normalized;
  }

  private getUsernameFromEmail(email?: string | null): string | null {
    const normalizedEmail = this.normalizeUsername(email);
    if (!normalizedEmail || !normalizedEmail.includes('@')) {
      return null;
    }
    const localPart = normalizedEmail.split('@')[0]?.trim();
    return localPart ? localPart : null;
  }

  private readOidcUserInfo(): Record<string, any> | null {
    try {
      const userInfoStr = localStorage.getItem('oidc_user_info');
      if (!userInfoStr) {
        return null;
      }
      const parsed = JSON.parse(userInfoStr);
      return typeof parsed === 'object' && parsed !== null ? parsed : null;
    } catch {
      return null;
    }
  }

  private getCachedUsername(userId: string): string | null {
    const cachedInMemory = this.memoryUsernameCache.get(userId);
    const normalizedInMemory = this.normalizeUsername(cachedInMemory);
    if (normalizedInMemory) {
      return normalizedInMemory;
    }

    try {
      const persisted = localStorage.getItem(this.getCacheKey(userId));
      const normalizedPersisted = this.normalizeUsername(persisted);
      if (normalizedPersisted) {
        this.memoryUsernameCache.set(userId, normalizedPersisted);
        return normalizedPersisted;
      }
    } catch {
      // Ignore localStorage failures and continue with other fallbacks
    }
    return null;
  }

  rememberUsername(userId: string, username?: string | null): string | null {
    const normalizedUserId = this.normalizeUsername(userId);
    const normalizedUsername = this.normalizeUsername(username);
    if (!normalizedUserId || !normalizedUsername) {
      return null;
    }

    this.memoryUsernameCache.set(normalizedUserId, normalizedUsername);
    try {
      localStorage.setItem(this.getCacheKey(normalizedUserId), normalizedUsername);
    } catch {
      // Ignore localStorage failures and keep in-memory cache
    }
    return normalizedUsername;
  }

  private async getUsernameFromLocalRecords(userId: string): Promise<string | null> {
    try {
      const latestComment = await commentTable
        .where('user_id')
        .equals(userId)
        .reverse()
        .sortBy('created_at');
      const latestCommentUsername = this.normalizeUsername(latestComment[0]?.username);
      if (latestCommentUsername) {
        return this.rememberUsername(userId, latestCommentUsername);
      }

      const latestHazard = (await hazardGateway.list()).find((hazard) => hazard.user_id === userId);
      const latestHazardUsername = this.normalizeUsername(latestHazard?.username);
      if (latestHazardUsername) {
        return this.rememberUsername(userId, latestHazardUsername);
      }
    } catch {
      // If IndexedDB lookup fails, proceed without this fallback
    }
    return null;
  }

  async resolveUsernameForUser(
    userId?: string | null,
    candidates?: { username?: string | null; displayName?: string | null; email?: string | null }
  ): Promise<string | null> {
    const normalizedUserId = this.normalizeUsername(userId);
    if (!normalizedUserId) {
      return null;
    }

    // Always prefer the locally stored current user's profile info first
    // This ensures updates made by the current user reflect immediately
    // even if backend data containing denormalized old usernames is fetched.
    const oidcInfo = this.readOidcUserInfo();
    if (oidcInfo && String(oidcInfo.userId || '') === normalizedUserId) {
      const oidcName =
        this.normalizeUsername(oidcInfo.displayName) ||
        this.normalizeUsername(oidcInfo.userDetails) ||
        this.normalizeUsername(oidcInfo.preferred_username) ||
        this.getUsernameFromEmail(oidcInfo.email);
      if (oidcName) {
        return this.rememberUsername(normalizedUserId, oidcName);
      }
    }

    const candidateName = this.normalizeUsername(candidates?.displayName) || this.normalizeUsername(candidates?.username);
    if (candidateName) {
      return this.rememberUsername(normalizedUserId, candidateName);
    }

    const cached = this.getCachedUsername(normalizedUserId);
    if (cached) {
      return cached;
    }

    const candidateFromEmail = this.getUsernameFromEmail(candidates?.email);
    if (candidateFromEmail) {
      return this.rememberUsername(normalizedUserId, candidateFromEmail);
    }

    return this.getUsernameFromLocalRecords(normalizedUserId);
  }

  async resolveCurrentUsername(): Promise<string | null> {
    const oidcInfo = this.readOidcUserInfo();
    if (!oidcInfo) {
      return null;
    }

    const userId = this.normalizeUsername(oidcInfo.userId);
    if (!userId) {
      return null;
    }

    const preferredName =
      this.normalizeUsername(oidcInfo.displayName) ||
      this.normalizeUsername(oidcInfo.userDetails) ||
      this.normalizeUsername(oidcInfo.preferred_username) ||
      this.getUsernameFromEmail(oidcInfo.email);

    if (preferredName) {
      return this.rememberUsername(userId, preferredName);
    }

    return this.resolveUsernameForUser(userId);
  }

  private getAuthHeaders(): Record<string, string> {
    return getAuthHeaders();
  }

  async getUserProfile(userId?: string): Promise<UserProfile> {
    if (!userId) {
      if (this.profileSelfInflight) {
        return this.profileSelfInflight;
      }
      const p = this.fetchUserProfileFromApi(undefined).finally(() => {
        if (this.profileSelfInflight === p) {
          this.profileSelfInflight = null;
        }
      });
      this.profileSelfInflight = p;
      return p;
    }
    return this.fetchUserProfileFromApi(userId);
  }

  private async fetchUserProfileFromApi(userId?: string): Promise<UserProfile> {
    try {
      const url = userId
        ? `${API_BASE_URL}/api/getUserProfile?userId=${encodeURIComponent(userId)}`
        : `${API_BASE_URL}/api/getUserProfile`;

      const response = await fetchWithAuth(url, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: { message: 'Unknown error' } }));
        throw new Error(errorData.error?.message || `Failed to fetch user profile: ${response.status}`);
      }

      const data = await response.json();
      return data as UserProfile;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  }

  async updateUserProfile(updates: UpdateProfileRequest): Promise<UserProfile> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/updateUserProfile`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: { message: 'Unknown error' } }));
        throw new Error(errorData.error?.message || `Failed to update user profile: ${response.status}`);
      }

      const data = await response.json();
      
      // Update local storage so new hazards use the new name
      try {
        const userInfoStr = localStorage.getItem('oidc_user_info');
        if (userInfoStr) {
          const userInfo = JSON.parse(userInfoStr);
          userInfo.displayName = data.profile.displayName;
          userInfo.userDetails = data.profile.displayName; // Some parts use userDetails
          if (data.profile.profileImageUrl) {
            userInfo.profileImageUrl = data.profile.profileImageUrl;
          }
          localStorage.setItem('oidc_user_info', JSON.stringify(userInfo));
        }
        
        // Update all local hazards by this user
        const userId = data.profile.userId;
        if (userId) {
          this.rememberUsername(userId, data.profile.displayName || data.profile.username);

          // Notify already-mounted views (Community feed, nav avatar, etc.) so
          // the new display name / avatar appear immediately without a hard refresh.
          eventEmitter.emit(EVENTS.PROFILE_UPDATED, {
            userId,
            username: data.profile.displayName,
            displayName: data.profile.displayName,
            profileImageUrl: data.profile.profileImageUrl || undefined,
          });
        }
      } catch (e) {
        console.error('Failed to update local cache after profile update', e);
      }

      return data.profile as UserProfile;
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  }

  /**
   * Convert File to base64 string for upload
   */
  async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove data:image/...;base64, prefix
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }
}

const userProfileService = new UserProfileService();
export default userProfileService;

